/**
 * Surface Reconnaissance & High-Resolution Image Inspector Modal
 * Features interactive magnifying loupe for inspecting meter-scale
 * lunar surface features, geological stratigraphy, and astrological dossiers.
 */

import { formatLat, formatLon } from '../utils/math.js';

export class ReconModal {
  constructor(container, onFlyTo = null) {
    this.container = container;
    this.onFlyTo = onFlyTo;

    this.modal = document.createElement('div');
    this.modal.className = 'recon-modal-backdrop hidden';
    this.container.appendChild(this.modal);

    this.currentFeature = null;
    this.render();
  }

  render() {
    this.modal.innerHTML = `
      <div class="recon-modal-content glass-card">
        <div class="recon-modal-header">
          <div class="title-wrap">
            <span class="badge" id="recon-category">CREWED MISSION</span>
            <h2 id="recon-title">Apollo 11 — Tranquillity Base</h2>
          </div>
          <button class="close-btn" id="recon-close" aria-label="Close Inspector">&times;</button>
        </div>

        <div class="recon-modal-body">
          <!-- Left: High-Resolution LROC Image Viewer with Magnifying Loupe -->
          <div class="recon-image-container">
            <div class="recon-image-wrapper" id="recon-img-wrapper">
              <img id="recon-image" src="" alt="Lunar Reconnaissance Photography" />
              <div class="recon-loupe" id="recon-loupe"></div>
              <div class="recon-badge">LROC HIGH-RES NAC RECONNAISSANCE</div>
            </div>
            <div class="loupe-hint">🔍 Hover over image to activate 3.5× Magnifying Surface Loupe</div>
            <p class="recon-caption" id="recon-caption"></p>
          </div>

          <!-- Right: Selenological & Astrological Dossier -->
          <div class="recon-dossier">
            <div class="dossier-section stats-grid">
              <div class="stat-card">
                <span class="stat-label">COORDINATES</span>
                <span class="stat-val" id="recon-coords">0.67° N, 23.47° E</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">GEOLOGICAL ERA</span>
                <span class="stat-val" id="recon-era">Upper Imbrian</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">DIAMETER / SPAN</span>
                <span class="stat-val" id="recon-diameter">873 km</span>
              </div>
              <div class="stat-card">
                <span class="stat-label">DEPTH / RELIEF</span>
                <span class="stat-val" id="recon-depth">1.5 km</span>
              </div>
            </div>

            <div class="dossier-section">
              <h3>🔭 Selenological Overview</h3>
              <p id="recon-description"></p>
              <div class="callout scientific">
                <strong>Scientific Significance:</strong>
                <p id="recon-scientific"></p>
              </div>
            </div>

            <div class="dossier-section">
              <h3>✨ Astrological & Esoteric Resonance</h3>
              <div class="callout astrological">
                <p id="recon-astrological"></p>
              </div>
            </div>

            <div class="dossier-section" id="recon-landmarks-section">
              <h3>📍 Distinct Surface Features</h3>
              <ul class="landmarks-list" id="recon-landmarks"></ul>
            </div>

            <div class="modal-actions">
              <button class="action-btn primary" id="recon-fly-btn">🚀 Orbit Around Feature</button>
              <button class="action-btn secondary" id="recon-dismiss-btn">Dismiss</button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.initEvents();
  }

  initEvents() {
    this.modal.querySelector('#recon-close').addEventListener('click', () => this.hide());
    this.modal.querySelector('#recon-dismiss-btn').addEventListener('click', () => this.hide());

    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.hide();
    });

    this.modal.querySelector('#recon-fly-btn').addEventListener('click', () => {
      if (this.currentFeature && this.onFlyTo) {
        this.onFlyTo(this.currentFeature);
        this.hide();
      }
    });

    // Setup Magnifying Loupe
    const wrapper = this.modal.querySelector('#recon-img-wrapper');
    const img = this.modal.querySelector('#recon-image');
    const loupe = this.modal.querySelector('#recon-loupe');

    wrapper.addEventListener('mouseenter', () => {
      loupe.style.display = 'block';
      loupe.style.backgroundImage = `url('${img.src}')`;
    });

    wrapper.addEventListener('mouseleave', () => {
      loupe.style.display = 'none';
    });

    wrapper.addEventListener('mousemove', (e) => {
      const rect = wrapper.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const loupeRadius = 60; // half of 120px
      loupe.style.left = `${x - loupeRadius}px`;
      loupe.style.top = `${y - loupeRadius}px`;

      // Zoom ratio (3.5x)
      const zoom = 3.5;
      loupe.style.backgroundSize = `${rect.width * zoom}px ${rect.height * zoom}px`;
      loupe.style.backgroundPosition = `-${x * zoom - loupeRadius}px -${y * zoom - loupeRadius}px`;
    });
  }

  show(feature) {
    this.currentFeature = feature;

    this.modal.querySelector('#recon-title').textContent = feature.name;
    this.modal.querySelector('#recon-category').textContent = feature.category || feature.type.toUpperCase();
    this.modal.querySelector('#recon-coords').textContent = `${formatLat(feature.lat)}, ${formatLon(feature.lon)}`;
    this.modal.querySelector('#recon-era').textContent = feature.geologicalEra || 'Unclassified';
    this.modal.querySelector('#recon-diameter').textContent = feature.diameterKm > 0 ? `${feature.diameterKm} km` : 'Landing Site';
    this.modal.querySelector('#recon-depth').textContent = feature.depthKm > 0 ? `${feature.depthKm} km` : 'Surface Level';

    this.modal.querySelector('#recon-description').textContent = feature.description;
    this.modal.querySelector('#recon-scientific').textContent = feature.scientificSignificance;
    this.modal.querySelector('#recon-astrological').textContent = feature.astrologicalSignificance;
    this.modal.querySelector('#recon-caption').textContent = feature.reconCaption || '';

    const img = this.modal.querySelector('#recon-image');
    img.src = feature.reconImage || './assets/recon/apollo11.jpg';

    const landmarksList = this.modal.querySelector('#recon-landmarks');
    landmarksList.innerHTML = '';
    if (feature.keyLandmarks && feature.keyLandmarks.length > 0) {
      feature.keyLandmarks.forEach(item => {
        const li = document.createElement('li');
        li.textContent = item;
        landmarksList.appendChild(li);
      });
      this.modal.querySelector('#recon-landmarks-section').style.display = 'block';
    } else {
      this.modal.querySelector('#recon-landmarks-section').style.display = 'none';
    }

    this.modal.classList.remove('hidden');
  }

  hide() {
    this.modal.classList.add('hidden');
    this.currentFeature = null;
  }
}
